import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getAuctionDetails } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { AssetsListWithNames } from './AssetsListWithNames';
import { NewBidForm } from './NewBidForm';

interface TradeAuctionModalProps {
  auctionId: number;
  onClose: () => void;
}

export function TradeAuctionModal({ auctionId, onClose }: TradeAuctionModalProps) {
  const [details, setDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data, error } = await getAuctionDetails(auctionId);
        if (error) throw error;
        if (mounted) setDetails(data ?? null);
      } catch (e) {
        console.error('TradeAuctionModal load error', e);
        toast.error('Impossible de charger les détails');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [auctionId]);

  const handleAcceptBid = async (bidId: number) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await fetch('/api/placeholder-accept'); // placeholder to keep actionLoading UX; we avoid SQL changes here
      toast.success(`Simulé: Offre ${bidId} acceptée`);
    } catch (e) {
      toast.error('Erreur acceptation (simulé)');
    } finally {
      setActionLoading(false);
    }
  };

  const modal = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.45)', padding: 24 }}>
      <div style={{ position: 'relative', zIndex: 2147483648, width: '100%', maxWidth: 1100, pointerEvents: 'auto' }}>
        <div className="modal-box" style={{ backgroundColor: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.35)', borderRadius: 12, padding: '1rem', maxHeight: '90vh', overflow: 'auto' }}>
          {loading || !details ? (
            <div className="flex justify-center py-8"><span className="loading loading-spinner loading-lg" /></div>
          ) : (
            <div>
              {/* Header (blue) */}
              <div className="modal-header bg-primary text-white rounded-md mb-4 p-3 flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold mb-1">🔥 Enchère #{auctionId}</h2>
                  <div className="small opacity-75">{details.auction?.initiator_username} → {details.auction?.target_username}</div>
                  <div className="small opacity-50">🏒 {details.auction?.league_name ?? 'League'}</div>
                </div>
                <button type="button" className="btn btn-close btn-close-white" onClick={onClose} aria-label="Close" />
              </div>

              {/* Explanation (info alert) */}
              <div className="alert alert-info mb-4 d-flex align-items-start gap-2">
                <span>💡</span>
                <div className="small">
                  <strong>Détails de l'enchère</strong>
                  <p className="mb-0">Consultez la transaction originale, les contre-offres reçues et proposez votre propre offre ci-dessous.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left: Original transaction */}
                <div className="col-span-2 p-3 border rounded" style={{ borderColor: 'rgba(59,130,246,.3)' }}>
                  <h3 className="font-semibold mb-3 text-primary">📋 Transaction originale</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm opacity-70 mb-2">Initiateur</div>
                      <div className="mb-2 font-medium">{details.auction?.initiator_username ?? `DG #${details.auction?.initiator_user_id}`}</div>
                      <AssetsListWithNames assets={details.auction?.original_sender_assets || {}} compact />
                    </div>
                    <div>
                      <div className="text-sm opacity-70 mb-2">Cible</div>
                      <div className="mb-2 font-medium">{details.auction?.target_username ?? `DG #${details.auction?.target_user_id}`}</div>
                      <AssetsListWithNames assets={details.auction?.original_receiver_assets || {}} compact />
                    </div>
                  </div>
                </div>

                {/* Right: Bids list */}
                <div className="col-span-1">
                  <h3 className="font-semibold mb-3">🎯 Contre-offres ({(details.bids || []).length})</h3>
                  <div className="space-y-3">
                    {(details.bids || []).length === 0 ? (
                      <div className="text-center py-8 opacity-60">Aucune contre-offre</div>
                    ) : (
                      (details.bids || []).map((bid: any) => (
                        <div key={bid.bid_id} className="p-3 border rounded" style={{ borderColor: 'rgba(34,197,94,0.08)' }}>
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-semibold">{bid.bidder_username ?? `DG #${bid.bidder_user_id}`}</div>
                            <div className="text-xs opacity-70">{new Date(bid.created_at).toLocaleString('fr-CA')}</div>
                          </div>
                          <div className="text-sm mb-2">Offre :</div>
                          <AssetsListWithNames assets={bid.bid_sender_assets || {}} compact />
                          <div className="text-sm mt-2">Demande :</div>
                          <AssetsListWithNames assets={bid.bid_receiver_assets || {}} compact />
                          <div className="mt-3 flex gap-2 justify-end">
                            <button className="btn btn-sm btn-success" onClick={() => handleAcceptBid(bid.bid_id)} disabled={actionLoading}>✅ Accepter</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* New bid form */}
              <div className="mt-6">
                <NewBidForm auctionId={auctionId} leagueId={details.auction?.league_id} onBidPlaced={() => { /* reload later */ }} originalReceiverAssets={details.auction?.original_receiver_assets} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modal, document.body);
}